import { Button } from 'primereact/button';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { IndividualStudentReportPdfQuery } from '../../graphQLOperations/__generated__/IndividualStudentReportPdfQuery.graphql';
import { getPdfIndividualStudent } from '../../graphQLOperations/IndividualStudentReport';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { IndividualSingleOrBulk, ModalSelectedValues } from './IndividualStudentReportPdfModal';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectedIndividualStudentReportState,
  setIndividualStudentReport,
} from '../../redux/reducers/individualStudentReportSlice';

const BATCH_SIZE_SUMMARY = 25;
const BATCH_SIZE_DETAILED = 10;
interface Props {
  userIdsToPDF: string[] | null;
  pdfSkip: boolean;
  modalSelectedValues: ModalSelectedValues;
  setPdfSkip: Dispatch<SetStateAction<boolean>>;
  resetModal: () => void;
  isSummary: boolean;
}

export const DownloadIndividualReportPdf: FC<Props> = ({
  userIdsToPDF,
  pdfSkip,
  modalSelectedValues,
  setPdfSkip,
  resetModal,
  isSummary,
}) => {
  const getBatchSize = () => (isSummary ? BATCH_SIZE_SUMMARY : BATCH_SIZE_DETAILED);
  const [tempStudentList, setTempStudentList] = useState<string[] | null>(null);
  const [offSet, setOffSet] = useState(0);
  const dispatch = useAppDispatch();
  const isrRedux = useAppSelector(selectedIndividualStudentReportState);
  const { generateIndividualStudentReportPDFs } = useLazyLoadQuery<IndividualStudentReportPdfQuery>(
    getPdfIndividualStudent,
    {
      input: {
        skillLearningActivityStartDate: isrRedux?.dates ? isrRedux.dates[0] : null,
        skillLearningActivityEndDate: isrRedux?.dates ? new Date(isrRedux.dates[1].toLocaleDateString('en-US')) : null,
        userIds:
          modalSelectedValues.singleOrBulkType === IndividualSingleOrBulk.BULK
            ? tempStudentList?.map(el => `UserNode:${splitId(el as string)}`) || []
            : userIdsToPDF?.map(id => `UserNode:${splitId(id)}`) || [],
        isSummary,
        withAcknowledgment: modalSelectedValues.signature,
      },
      skip: pdfSkip || tempStudentList?.length === 0,
    },
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    const initialOffSetIncrement = modalSelectedValues.selectedStudents
      ? modalSelectedValues.selectedStudents.length % getBatchSize() === 1
        ? 1
        : 0
      : 0;
    setTempStudentList(modalSelectedValues.selectedStudents?.slice(0, getBatchSize() + initialOffSetIncrement) || null);
    setOffSet(getBatchSize() + initialOffSetIncrement);
  }, [modalSelectedValues.selectedStudents, isSummary]);

  useEffect(() => {
    if (generateIndividualStudentReportPDFs?.base64Data) {
      const downloadLink = document.createElement('a');
      downloadLink.href = 'data:application/pdf;base64,' + generateIndividualStudentReportPDFs?.base64Data;
      downloadLink.download = generateIndividualStudentReportPDFs.fileName;
      downloadLink.click();
      const temp = modalSelectedValues.selectedStudents?.slice(offSet, offSet + getBatchSize());
      if (!temp || temp.length === 0) {
        setPdfSkip(true);
        dispatch(setIndividualStudentReport({ ...isrRedux, isLoadingPdf: false }));
        return resetModal();
      }

      setTempStudentList(temp);
      setOffSet(prev => prev + getBatchSize());

      if (modalSelectedValues.singleOrBulkType === IndividualSingleOrBulk.SINGLE) {
        setPdfSkip(true);
        dispatch(setIndividualStudentReport({ ...isrRedux, isLoadingPdf: false }));
        return resetModal();
      }
    }
  }, [generateIndividualStudentReportPDFs?.base64Data]);

  return (
    <Button
      id="dlIndividualStudentReport"
      label="Download"
      className="tw-float-right"
      disabled={
        isrRedux.isLoadingPdf ||
        modalSelectedValues.reportType === null ||
        modalSelectedValues.singleOrBulkType === null ||
        (modalSelectedValues.singleOrBulkType === IndividualSingleOrBulk.BULK &&
          !modalSelectedValues.selectedStudents?.length)
      }
      onClick={() => {
        dispatch(setIndividualStudentReport({ ...isrRedux, isLoadingPdf: true }));
        setPdfSkip(false);
      }}
    />
  );
};
